import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage-service';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { ExtratoDesligamentoPayload, ExtratoDesligamentoResponse, ExtratoDesligamentoTokenResponse } from '../../core/models/extrato-desligamento';
import { TokenApiResponse } from '../../core/models/token-api-response';
import { HomeApiResponse } from '../../core/models/home';
import { EscolhaInstitutoApiResponse } from '../../core/models/escolha-instituto';
import { DadosCadastraisPayload, DadosCadastraisResponse } from '../../core/models/dados-cadastrais';
import { ACCESS_TOKEN_KEY } from '../../core/constants/storage-keys';
import { ModalidadeIrApiResponse, ModalidadeIrPayload } from '../../core/models/modalidade-ir';
import { DocumentosEnviadosResponse, DocumentoUpload } from '../../core/models/documento';
import { DetalhesMVPResponse, DetalhesResponse, ResumoResponse } from '../../core/models/resumo';
import { ParametrosTermo, TermoAssinadoResponse, TermoResponse } from '../../core/models/termo';
import { SalarioMvpResponse } from '../../core/models/autopatrocinio';
import { ParticipanteAccessToken } from '../../core/models/participante-access-token';
import { CancelaInstitutoResponse } from '../../core/models/cancela-instituto';
import { gerarModalidadeIrMock } from '../../core/mocks/modalidade-ir-mock';

// TODO: voltar para false (e remover o mock) quando a API de escolha de IR estiver disponível.
const USAR_MOCK_MODALIDADE_IR = false;

@Injectable({
  providedIn: 'root',
})
export class HttpClientService {
  constructor(
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService
  ) { }

  /** Usado pela área administrativa (home-adm) */
  async getCompanies():  Promise<HttpResponse<any>> {
    const headers = new HttpHeaders({
      "Content-Type":"application/json",
      "Ocp-Apim-Subscription-Key": `${environment.ocpapimsubscriptionkeyBaseUnificada}`
    });
    const url = `${environment.baseUrl}/apis/api-baseunificada-cadastro-parametro/v1/Empresas`;
    return  lastValueFrom(this.httpClient.get<any>(url, {headers, observe: 'response' }));
  }


  /** Busca um novo token de acesso a partir do token recebido na URL do portal. */
  async getNovoToken(token: string): Promise<HttpResponse<TokenApiResponse>> {
    const headers = new HttpHeaders({
      "Authorization": "Bearer " + token,
      "Ocp-Apim-Subscription-Key": `${environment.ocpapimsubscriptionkeyPortal}`
    });
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/auth/portal`;
    return lastValueFrom(this.httpClient.post<TokenApiResponse>(url, null, {headers, observe: 'response' }));
  }

  async getHomeData(): Promise<HttpResponse<HomeApiResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkeyPrevidencia);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/home`;
    return lastValueFrom(
      this.httpClient.get<HomeApiResponse>(url, { headers, observe: 'response' })
    );
  }

  async getInstituto(): Promise<HttpResponse<EscolhaInstitutoApiResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/escolha-instituto`;
    return lastValueFrom(
      this.httpClient.get<EscolhaInstitutoApiResponse>(url, { headers, observe: 'response' })
    );
  }

  async saveInstituto(id: number): Promise<HttpResponse<any>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/escolha-instituto`;
    return lastValueFrom(
      this.httpClient.put<any>(url, {"idTipoSolicitacao": id}, { headers, observe: 'response' }),
    );
  }

  /**
   * Busca os dados cadastrais do participante identificado pelo token de acesso,
   * junto com as listas de países e bancos usadas no formulário.
   */
  async getDadosCadastrais(): Promise<HttpResponse<DadosCadastraisResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/dados-cadastrais`;
    return lastValueFrom(
      this.httpClient.get<DadosCadastraisResponse>(url, { headers, observe: 'response' })
    );
  }

  async saveDadosCadastrais(payload: DadosCadastraisPayload): Promise<HttpResponse<DadosCadastraisResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/dados-cadastrais`;
    return lastValueFrom(
      this.httpClient.post<DadosCadastraisResponse>(url, payload, { headers, observe: 'response' })
    );
  }

  /**
   * Busca a simulação de alíquota, IR e valor líquido de cada regime de IR (progressivo e regressivo),
   * a faixa de parcelas permitida e a escolha já feita anteriormente (regime e parcelas), se houver.
   */
  async getModalidadeIr(): Promise<HttpResponse<ModalidadeIrApiResponse>> {
    if (USAR_MOCK_MODALIDADE_IR) {
      return new HttpResponse({ status: 200, body: gerarModalidadeIrMock() });
    }

    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/escolha-ir`;
    return lastValueFrom(
      this.httpClient.get<ModalidadeIrApiResponse>(url, { headers, observe: 'response' })
    );
  }

  /** Salva o regime de IR e a quantidade de parcelas escolhidos pelo participante. */
  async saveModalidadeIr(payload: ModalidadeIrPayload): Promise<HttpResponse<unknown>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/escolha-ir`;
    return lastValueFrom(
      this.httpClient.put<unknown>(url, payload, { headers, observe: 'response' })
    );
  }

  /** Assina o termo enviando no body o processId retornado pelo getTermo ({ processId: 459266 }). */
  async assinarTermo(processId: number): Promise<HttpResponse<TermoAssinadoResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/revise-assine-termo`;
    const body = { processId };
    return lastValueFrom(
      this.httpClient.post<TermoAssinadoResponse>(url, body, { headers, observe: 'response' })
    );
  }

  /** Busca o resumo da solicitação (dados do participante, conta e valores do resgate). */
  async getResumo(): Promise<HttpResponse<ResumoResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/revisao-cadastral`;
    return lastValueFrom(
      this.httpClient.get<ResumoResponse>(url, { headers, observe: 'response' })
    );
  }

  async saveResumo(): Promise<HttpResponse<ResumoResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/revisao-solicitacao`;
    return lastValueFrom(
      this.httpClient.post<ResumoResponse>(url, null, { headers, observe: 'response' })
    );
  }

  async getDetalhes(): Promise<HttpResponse<DetalhesResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/comprovante`;
    return lastValueFrom(
      this.httpClient.get<DetalhesResponse>(url, { headers, observe: 'response' })
    );
  }

  async getDetalhesMVP(): Promise<HttpResponse<DetalhesMVPResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/temporario/comprovante`;
    return lastValueFrom(
      this.httpClient.get<DetalhesMVPResponse>(url, { headers, observe: 'response' })
    );
  }
  /**
   * Busca os documentos que o participante já enviou e os tipos que ele precisa enviar.
   * `documentos` vazio significa que nenhum arquivo foi enviado ainda.
   */
  async getDocumentosEnviados(): Promise<HttpResponse<DocumentosEnviadosResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/anexe-docs`;
    return lastValueFrom(
      this.httpClient.get<DocumentosEnviadosResponse>(url, { headers, observe: 'response' })
    );
  }

  /**
   * Envia um documento por vez, como multipart/form-data (tipoDocumento + arquivo). Responde com os
   * mesmos campos de getDocumentosEnviados, já com o documento enviado.
   */
  async saveDocumento({ tipoDocumento, arquivo }: DocumentoUpload): Promise<HttpResponse<DocumentosEnviadosResponse>> {
    // Sem Content-Type: com FormData o navegador monta o multipart/form-data com o boundary.
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey).delete('Content-Type');
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/anexe-docs`;
    const body = new FormData();
    body.append('tipoDocumento', tipoDocumento);
    body.append('arquivo', arquivo, arquivo.name);
    return lastValueFrom(
      this.httpClient.post<DocumentosEnviadosResponse>(url, body, { headers, observe: 'response' })
    );
  }

  /** Usado pela área administrativa (home-adm), onde empresa/plano/cpf/matrícula vêm de um formulário. */
  async gerarExtratoDesligamento(
    payload: ExtratoDesligamentoPayload
  ): Promise<HttpResponse<ExtratoDesligamentoResponse>> {
    const headers = this.getHeaderDefault();
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/extratos/desligamento`;
    return lastValueFrom(
      this.httpClient.post<ExtratoDesligamentoResponse>(url, payload, { headers, observe: 'response' })
    );
  }

  async cancelarEscolhaInstituto(): Promise<HttpResponse<CancelaInstitutoResponse>> {
    const headers = this.getHeaderDefault();
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/cancelar`;
    return lastValueFrom(
      this.httpClient.post<CancelaInstitutoResponse>(url, null, { headers, observe: 'response' })
    );
  }

  /**
   * Usado pela página do participante (extrato-desligamento), onde a API identifica o
   * participante pelo token de acesso obtido em token-verify (enviado via header Authorization
   * por getHeaderDefault), não sendo necessário enviar payload.
   */
  async gerarExtratoDesligamentoPorToken(): Promise<HttpResponse<ExtratoDesligamentoTokenResponse>> {
    const headers = this.getHeaderDefault();
    const url = `${environment.baseUrl}/${environment.apiUrl}/v2/extratos/desligamento`;
    return lastValueFrom(
      this.httpClient.post<ExtratoDesligamentoTokenResponse>(url, null, { headers, observe: 'response' })
    );
  }

  async getCep(cep: string): Promise<HttpResponse<any>>{
    const url = `https://viacep.com.br/ws/${cep}/json/`;
    return lastValueFrom(this.httpClient.get<any>(url));
  }

  /** Busca o termo a ser assinado (PDF em base64) e os dados da assinatura. */
  async getTermo(): Promise<HttpResponse<TermoResponse>> {
    const headers = this.getHeaderDefault();
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/revise-assine-termo`;
    return lastValueFrom(
      this.httpClient.get<TermoResponse>(url, { headers, observe: 'response' }),
    );
  }

  /**
   * Busca o termo do instituto, enviando o idInstituto como query param. No resgate (1) e no
   * autopatrocínio (2), envia também as escolhas da tela de origem (ex.: ?idInstituto=1&idBanco=237&
   * agencia=1234&conta=56789&regime=progressivo&parcelas=3&resideExterior=false ou ?idInstituto=2&
   * salario=5000&percentualBasica=4&valorTotal=400&mesesSuspensao=6); no BPD (3), só o idInstituto.
   * Campos nulos não são enviados.
   * TODO: API ainda não publicada; confirmar a URL e os nomes dos parâmetros com o contrato.
   */
  async getTermoMVP(
    idInstituto: number,
    escolha?: ParametrosTermo,
  ): Promise<HttpResponse<TermoResponse>> {
    const headers = this.getHeaderDefault();
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/temporario/revise-assine-termo`;
    const params = Object.entries(escolha ?? {}).reduce(
      (acc, [chave, valor]) => (valor === null || valor === undefined ? acc : acc.set(chave, String(valor))),
      new HttpParams().set('idInstituto', idInstituto),
    );
    return lastValueFrom(
      this.httpClient.get<TermoResponse>(url, { headers, params, observe: 'response' }),
    );
  }

  /**
   * Assina o termo do MVP com POST na mesma URL da busca, enviando o idInstituto e o processId
   * retornado pelo getTermoMVP como query params (?IdInstituto=2&ProcessId=459266).
   */
  async assinarTermoMVP(idInstituto: number, processId: number): Promise<HttpResponse<any>> {
    const headers = this.getHeaderDefault();
    const url = `${environment.baseUrl}/${environment.apiUrl}/v1/instituto-online/temporario/revise-assine-termo`;
    const params = new HttpParams().set('IdInstituto', idInstituto).set('ProcessId', processId);
    return lastValueFrom(
      this.httpClient.post<any>(url, null, { headers, params, observe: 'response' })
    );
  }

  /**
   * Busca os dados do participante desligado; o salário do autopatrocínio vem em
   * contribuicaoDesligado.autopatrocinio.baseCalculo.
   */
  async getSalarioMVP(participante: ParticipanteAccessToken): Promise<HttpResponse<SalarioMvpResponse>> {
    const headers = this.getHeaderDefault(environment.ocpapimsubscriptionkey);
    const url = `${environment.baseUrl}/apis/api-hub-previdencia/v1/participantes`;
    const params = new HttpParams({
      fromObject: {
        cpf: participante.cpf,
        idPlanoUnificado: participante.idPlanoUnificado,
        idPatrocinadoraUnificado: participante.idPatrocinadoraUnificado,
        matricula: participante.matricula,
        incluirContribuicaoDesligado: true,
      },
    });
    return lastValueFrom(
      this.httpClient.get<SalarioMvpResponse>(url, { headers, params, observe: 'response' }),
    );
  }

  getHeaderDefault(subscriptionKey: string = environment.ocpapimsubscriptionkey): HttpHeaders {
    const token = this.localStorageService.get(ACCESS_TOKEN_KEY);
    const headers = new HttpHeaders({
      ...(token ? { Authorization: token } : {}),
      "Content-Type":"application/json",
      "Ocp-Apim-Subscription-Key": subscriptionKey
    });
    return headers;
  }
}
